import React, { useEffect } from "react";
import CoinChart from "./CoinChart";
import PercentChange from "./PercentChange";
import { X, ExternalLink } from "lucide-react";

const CoinModal = ({ coin, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!coin) return null;

  const priceFormatter = (num) => {
    if (!num) return "-";
    if (num < 0.01) return num.toFixed(6) + " $";
    if (num < 1) return num.toFixed(4) + " $";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const formatSupply = (num) => {
    if (!num) return "Not specified";
    if (num >= 1e9) return (num / 1e9).toFixed(2) + " B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + " M";
    return num.toLocaleString();
  };

  return (
    <div className="coin-modal-overlay" onClick={onClose}>
      <div className="coin-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-coin-title">
            <img src={coin.image} alt={coin.name} className="modal-coin-logo" />
            <div>
              <div className="name-row">
                <h2>{coin.name}</h2>
                <span className="modal-ticker">{coin.symbol.toUpperCase()}</span>
                {coin.market_cap_rank && (
                  <span className="modal-rank-badge">#{coin.market_cap_rank}</span>
                )}
              </div>
              <div className="price-row">
                <span className="modal-current-price">{priceFormatter(coin.current_price)}</span>
                <PercentChange percent={coin.market_cap_change_percentage_24h} />
              </div>
            </div>
          </div>

          <button className="modal-close-btn" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Chart View */}
        <div className="modal-chart-section">
          <CoinChart coinId={coin.id} coinName={coin.name} />
        </div>

        {/* Deep Stats Breakdown Grid */}
        <div className="modal-stats-grid">
          <div className="modal-stat-box">
            <span className="stat-box-label">24h High</span>
            <span className="stat-box-value high">{priceFormatter(coin.high_24h)}</span>
          </div>

          <div className="modal-stat-box">
            <span className="stat-box-label">24h Low</span>
            <span className="stat-box-value low">{priceFormatter(coin.low_24h)}</span>
          </div>

          <div className="modal-stat-box">
            <span className="stat-box-label">Circulating Supply</span>
            <span className="stat-box-value">{formatSupply(coin.circulating_supply)}</span>
          </div>

          <div className="modal-stat-box">
            <span className="stat-box-label">All-Time High (ATH)</span>
            <div className="ath-box-val">
              <span className="stat-box-value">{priceFormatter(coin.ath)}</span>
              <PercentChange percent={coin.ath_change_percentage} />
            </div>
          </div>
        </div>

        {/* External Link */}
        <div className="modal-footer">
          <a
            href={`https://www.coingecko.com/en/coins/${coin.id.toLowerCase()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="external-gecko-btn"
          >
            <span>View full analysis on CoinGecko</span>
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CoinModal;
